// Static ids that may appear as analytics property values or autocapture
// attribute values. Everything else, labels and titles above all, is tenant
// content or product copy and never leaves the browser.
//
// The router's own vocabulary, its modes, admin views, tab kinds, dimensions
// and query params, comes from lib/routeModel.js, which imports nothing, so
// this file can read it from underneath the API client instead of keeping a
// copy. The vocabularies only analytics uses, dialogs, actions and attribute
// kinds, are defined here.
//
// So are event names and the closed values events carry, rather than beside
// the registry in events.js: the API client and the chat console name them
// from startup, and events.js loads only once analytics starts (client.js).
//
// Each vocabulary is an enum, referenced as NAME.KEY; the `*_IDS` lists are
// its values, for membership checks.
import {
    ADMIN_VIEW_MODES,
    BUILTIN_CANVAS_SLUGS,
    DIMENSION_IDS as ROUTE_DIMENSION_IDS,
    MODE_IDS as ROUTE_MODE_IDS,
    ROUTER_PARAM as ROUTE_PARAMS,
    SETTING_ADDRESSES,
    TAB_KINDS as ROUTE_TAB_KINDS,
} from "../routeModel.js";
import { defineEnum, defineLookup, enumValues } from "./enum.js";

// An id as an enum key: `chat-models` is CHAT_MODELS, `mcp_servers` is MCP_SERVERS.
function enumFromIds(name, ids) {
    return defineEnum(name, Object.fromEntries(ids.map((id) => [id.toUpperCase().replace(/[^A-Z0-9]+/g, "_"), id])));
}

export { ADMIN_VIEW_MODES };

export const ROUTER_PARAM = defineEnum("ROUTER_PARAM", ROUTE_PARAMS);

// Only routeModel.MODE_IDS controls reachable pages and navigation.
export const MODE = enumFromIds("MODE", ROUTE_MODE_IDS);
export const MODE_IDS = enumValues(MODE);

export const ADMIN_VIEW = enumFromIds("ADMIN_VIEW", Object.keys(ADMIN_VIEW_MODES));
export const ADMIN_VIEW_IDS = enumValues(ADMIN_VIEW);

// Settings' groups and sections, by address ("endpoints", "endpoints/install").
export const SETTING = enumFromIds("SETTING", SETTING_ADDRESSES);
export const SETTING_IDS = enumValues(SETTING);

export const TAB_KIND = enumFromIds("TAB_KIND", [...ROUTE_TAB_KINDS]);
export const TAB_KINDS = enumValues(TAB_KIND);

export const DIMENSION = enumFromIds("DIMENSION", ROUTE_DIMENSION_IDS);
export const DIMENSION_IDS = enumValues(DIMENSION);

// The controls a `ui_control_used` event names: a change to the URL that
// leaves the page the same (see PAGE_FIELDS in sanitize.js) is a control the
// person used. Each router param belongs to one control; a param without an
// entry is "other".
export const UI_CONTROL = defineEnum("UI_CONTROL", {
    RANGE: "range",
    SEARCH: "search",
    STEER: "steer",
    TABLE: "table",
    LAYOUT: "layout",
    FOCUS: "focus",
    TAB_SWITCH: "tab_switch",
    ENTITY: "entity",
    CANVAS: "canvas",
    CHAT: "chat",
    DETAIL: "detail",
    INVESTIGATION: "investigation",
    PAGE_TAB: "page_tab",
    DISCOVERY: "discovery",
    GENERATION: "generation",
    OTHER: "other",
});
export const UI_CONTROL_IDS = enumValues(UI_CONTROL);

export const UI_CONTROL_BY_PARAM = defineLookup({
    [ROUTE_PARAMS.FLEET_GROUP]: UI_CONTROL.ENTITY,
    [ROUTE_PARAMS.FLEET_VIEW]: UI_CONTROL.TAB_SWITCH,
    [ROUTE_PARAMS.FLEET_SEARCH]: UI_CONTROL.SEARCH,
    [ROUTE_PARAMS.FLEET_TYPE]: UI_CONTROL.ENTITY,
    [ROUTE_PARAMS.FLEET_LOCATION]: UI_CONTROL.ENTITY,
    [ROUTE_PARAMS.FLEET_EVIDENCE]: UI_CONTROL.ENTITY,
    [ROUTE_PARAMS.FLEET_SELECTED]: UI_CONTROL.DETAIL,
    [ROUTE_PARAMS.RANGE]: UI_CONTROL.RANGE,
    [ROUTE_PARAMS.QUERY]: UI_CONTROL.SEARCH,
    [ROUTE_PARAMS.TRACE_QUERY]: UI_CONTROL.SEARCH,
    [ROUTE_PARAMS.STEERS]: UI_CONTROL.STEER,
    // Entity-table search text, expanded rows and sort order.
    [ROUTE_PARAMS.UI]: UI_CONTROL.TABLE,
    [ROUTE_PARAMS.DCOL]: UI_CONTROL.LAYOUT,
    [ROUTE_PARAMS.XCOL]: UI_CONTROL.LAYOUT,
    [ROUTE_PARAMS.FOCUS]: UI_CONTROL.FOCUS,
    [ROUTE_PARAMS.ACTIVE]: UI_CONTROL.TAB_SWITCH,
    [ROUTE_PARAMS.STUDIO_ACTIVE]: UI_CONTROL.TAB_SWITCH,
    // Another entity of the same dimensions: the same page.
    [ROUTE_PARAMS.TABS]: UI_CONTROL.ENTITY,
    [ROUTE_PARAMS.SCOPE]: UI_CONTROL.ENTITY,
    [ROUTE_PARAMS.CANVAS]: UI_CONTROL.CANVAS,
    [ROUTE_PARAMS.CHAT]: UI_CONTROL.CHAT,
    [ROUTE_PARAMS.LOG]: UI_CONTROL.DETAIL,
    [ROUTE_PARAMS.HIST]: UI_CONTROL.DETAIL,
    [ROUTE_PARAMS.ORD]: UI_CONTROL.DETAIL,
    [ROUTE_PARAMS.TURN]: UI_CONTROL.DETAIL,
    [ROUTE_PARAMS.TAB]: UI_CONTROL.DETAIL,
    [ROUTE_PARAMS.PANEL]: UI_CONTROL.DETAIL,
    [ROUTE_PARAMS.FINDING]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.IFINDING]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.IGROUP]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.IEVIDENCE]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.ISUBJECT]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.INVESTIGATION]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.INVESTIGATION_PANEL]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.INVESTIGATION_STATUS]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.INVESTIGATING]: UI_CONTROL.INVESTIGATION,
    [ROUTE_PARAMS.UTAB]: UI_CONTROL.PAGE_TAB,
    [ROUTE_PARAMS.DTAB]: UI_CONTROL.PAGE_TAB,
    [ROUTE_PARAMS.GOVERNANCE_TAB]: UI_CONTROL.PAGE_TAB,
    [ROUTE_PARAMS.SECURITY_VIEW]: UI_CONTROL.PAGE_TAB,
    // Security Discovery's filters, grouping and layout.
    [ROUTE_PARAMS.DISCOVERY]: UI_CONTROL.DISCOVERY,
    [ROUTE_PARAMS.GEN]: UI_CONTROL.GENERATION,
});

// A canvas as analytics names it: a built-in canvas by its slug, any other
// canvas as custom, because its slug is the tenant's own text.
export const CUSTOM_CANVAS = "custom";
export const CANVAS_IDS = Object.freeze([...BUILTIN_CANVAS_SLUGS, CUSTOM_CANVAS]);

// Stands in for a scope dimension outside the vocabulary, so the depth still
// counts it without sending the unrecognized key.
export const UNKNOWN_DIMENSION = "unknown";

// The sub-views of the dashboard mode that get their own virtual path.
export const DASHBOARD_SUBVIEW = defineEnum("DASHBOARD_SUBVIEW", {
    CANVAS: "canvas",
    CHAT: "chat",
});

// Dialog ids are assigned at the dialog call sites. They name the dialog,
// never its title, because titles can interpolate tenant content.
export const DIALOG = defineEnum("DIALOG", {
    RUN_CONSENT: "run-consent",
    SIGN_OUT: "sign-out",
    CANVAS_SAVE: "canvas-save",
    EXTERNAL_LINK: "external-link",
    INTEGRATION_SYNC_HISTORY: "integration-sync-history",
    INTEGRATION_SYNC_SCOPE: "integration-sync-scope",
    ACTIVITY_ACCESS: "activity-access",
    ACTIVITY_GRANT: "activity-grant",
    ENDPOINT_ASSIGNMENT: "endpoint-assignment",
    ESCALATION_REQUEST: "escalation-request",
    NOTIFICATION_DESTINATION_NEW: "notification-destination-new",
    NOTIFICATION_DESTINATION_REMOVE: "notification-destination-remove",
    USER_INVITE: "user-invite",
    USER_INVITED: "user-invited",
    TOKEN_CREATE: "token-create",
    TOKEN_CREATED: "token-created",
    SKILL_UPLOAD: "skill-upload",
    SKILL_DEPLOY: "skill-deploy",
    SKILL_DELETE: "skill-delete",
    INVESTIGATION_DELETE: "investigation-delete",
    ACCESS_REVOKE: "access-revoke",
    CANVAS_DELETE: "canvas-delete",
    KERNEL_DELETE: "kernel-delete",
    ANTHROPIC_KEY_REMOVE: "anthropic-key-remove",
    OPENAI_KEY_REMOVE: "openai-key-remove",
    PERPLEXITY_WEBHOOK_REMOVE: "perplexity-webhook-remove",
    LANGSMITH_DISCONNECT: "langsmith-disconnect",
    COPILOT_DISCONNECT: "copilot-disconnect",
    VERTEX_DISCONNECT: "vertex-disconnect",
    LITELLM_REMOVE: "litellm-remove",
    AI_SERVICE_IGNORE: "ai-service-ignore",
    ROLLOUT_WAVE_CONFIRM: "rollout-wave-confirm",
    OTEL_MIRROR_EDIT: "otel-mirror-edit",
    CHAT_SETTINGS: "chat-settings",
    CHAT_DELETE: "chat-delete",
    TOKEN_REVOKE: "token-revoke",
    BROWSER_CAPTURE_STOP: "browser-capture-stop",
    OTEL_MIRROR_REMOVE: "otel-mirror-remove",
    ROLLOUT_ACTION_CONFIRM: "rollout-action-confirm",
    ROLLOUT_OVERRIDE_REMOVE: "rollout-override-remove",
    INTEGRATION_DELETE: "integration-delete",
    INTEGRATION_DISABLE: "integration-disable",
    USER_REMOVE: "user-remove",
    USER_ROLE_CHANGE: "user-role-change",
    USER_PASSWORD_RESET: "user-password-reset",
    GRANT_EDIT: "grant-edit",
    ESCALATION_DECIDE: "escalation-decide",
    ATTACHMENT_REMOVE: "attachment-remove",
});
export const DIALOG_IDS = enumValues(DIALOG);

export const ACTION = defineEnum("ACTION", {
    CONFIRM: "confirm",
    CANCEL: "cancel",
    FILTER_BUILDER: "filter-builder",
    NEW_CHAT: "new-chat",
    RANGE_PICKER: "range-picker",
    CHAT_PANEL: "chat-panel",
    CONTEXT_PANEL: "context-panel",
});
export const ACTION_IDS = enumValues(ACTION);

// The kinds of autocapture attribute `phAttrs()` writes, each checked
// against its vocabulary below.
export const ATTRIBUTE = defineEnum("ATTRIBUTE", {
    DIALOG: "dialog",
    ACTION: "action",
    NAV: "nav",
    NAV_MODE: "navMode",
    SETTING: "setting",
    TAB_KIND: "tabKind",
    DIM: "dim",
});

export const ATTRIBUTE_VOCABULARY = defineLookup({
    [ATTRIBUTE.DIALOG]: DIALOG_IDS,
    [ATTRIBUTE.ACTION]: ACTION_IDS,
    [ATTRIBUTE.NAV]: ADMIN_VIEW_IDS,
    [ATTRIBUTE.NAV_MODE]: MODE_IDS,
    [ATTRIBUTE.SETTING]: SETTING_IDS,
    [ATTRIBUTE.TAB_KIND]: TAB_KINDS,
    [ATTRIBUTE.DIM]: DIMENSION_IDS,
});

export function isKnownValue(kind, value) {
    const list = ATTRIBUTE_VOCABULARY[kind];
    return Array.isArray(list) && list.includes(value);
}

export const EVENT_NAME = defineEnum("EVENT_NAME", {
    PAGEVIEW: "$pageview",
    PAGELEAVE: "$pageleave",
    AUTOCAPTURE: "$autocapture",
    ELEMENT_VIEWED: "$element_viewed",
    EXCEPTION: "$exception",
    RAGECLICK: "$rageclick",
    HEATMAP: "$$heatmap",
    IDENTIFY: "$identify",
    GROUP_IDENTIFY: "$groupidentify",
    CHAT_TURN: "chat_turn",
    UI_CONTROL_USED: "ui_control_used",
    API_REQUEST: "api_request",
    DATA_QUERY_FAILED: "data_query_failed",
    ANALYTICS_THROTTLED: "analytics_throttled",
});

export const CHAT_TRIGGER = defineEnum("CHAT_TRIGGER", {
    PROMPT: "prompt",
    SLASH: "slash",
    STARTER: "starter",
    CONTINUATION: "continuation",
});

export const CHAT_SURFACE = defineEnum("CHAT_SURFACE", {
    DOCKED: "docked",
    OVERLAY: "overlay",
});

export const REQUEST_ERROR_KIND = defineEnum("REQUEST_ERROR_KIND", {
    TIMEOUT: "timeout",
    ABORT: "abort",
    AUTH: "auth",
    NETWORK: "network",
});

// Abort is classified so it can be skipped; it is never sent.
export const QUERY_ERROR_KIND = defineEnum("QUERY_ERROR_KIND", {
    ABORT: "abort",
    AUTH: "auth",
    DENIED: "denied",
    NETWORK: "network",
    QUERY: "query",
    SERVER: "server",
    TIMEOUT: "timeout",
    UNKNOWN: "unknown",
});

// Values of the SDK's `$exception_source` property that the window listeners set.
export const EXCEPTION_SOURCE = defineEnum("EXCEPTION_SOURCE", {
    WINDOW_ERROR: "window.onerror",
    UNHANDLED_REJECTION: "unhandledrejection",
});

// The dashboard feature gates reported in the `gates` super property.
export const GATE = defineEnum("GATE", {
    SECURITY_TAB: "security_tab",
    COMPARISONS: "comparisons",
    INVENTORY: "inventory",
    SKILLS: "skills",
    CHAT_PERSISTENCE: "chat_persistence",
});

export const GROUP_TYPE = defineEnum("GROUP_TYPE", {
    TENANT: "tenant",
});

// Failure-class events can loop: a query that fails on every poll, or a
// render error that recurs on every frame, would otherwise send hundreds of
// billable events a minute. Each event here has a cap per window, and the
// first drop in a window sends one `analytics_throttled` marker so the burst
// is visible in PostHog without its volume.
export const EVENT_RATE_LIMITS = Object.freeze({
    [EVENT_NAME.DATA_QUERY_FAILED]: Object.freeze({ limit: 20, windowMs: 60_000 }),
    [EVENT_NAME.EXCEPTION]: Object.freeze({ limit: 10, windowMs: 60_000 }),
});
